import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar as CalendarIcon, Clock, User, ExternalLink, MessageSquareText } from "lucide-react";
import api from "../../lib/api";
import FoldBlock from "./FoldBlock";

/**
 * MeetingsBlock — Wave 19 #106 Phase 1.
 *
 * Minutes-of-meeting archive scoped to a customer OR a lead.
 *
 * Reads from `/api/meetings?customer_id=...` or `?lead_id=...` (both
 * already supported by the meetings module). Each row is a clickable
 * card → opens the same MeetingDetailModal we wired into the calendar
 * (so the rep can read the breadcrumb, edit, capture another note, etc.)
 *
 * Past outcomes (the "minutes") are surfaced inline on each card so the
 * rep doesn't have to drill in to read them.
 */
export default function MeetingsBlock({
  customerId,
  leadId,
  defaultOpen = false,
}) {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const params = {};
      if (customerId) params.customer_id = customerId;
      if (leadId) params.lead_id = leadId;
      const { data } = await api.get("/meetings", { params });
      setMeetings(Array.isArray(data) ? data : []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load meetings");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!customerId && !leadId) return;
    load();
    // eslint-disable-next-line
  }, [customerId, leadId]);

  const fmt = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };

  return (
    <>
      <FoldBlock
        icon={CalendarIcon}
        iconColor="#A78BFA"
        title="Meetings"
        count={meetings.length}
        defaultOpen={defaultOpen}
        testId="meetings-block"
        emptyHint="No meetings yet. They appear here automatically when scheduled on the calendar."
        countAccent={meetings.length ? "border-[#A78BFA]/40 text-[#C4B5FD] bg-[#A78BFA]/10" : ""}
      >
        {err && (
          <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded px-2 py-1.5 mb-2">
            {err}
          </div>
        )}
        {loading && <div className="text-xs text-[#647087] py-2">Loading meetings…</div>}
        {!loading && meetings.length > 0 && (
          <ul className="space-y-2" data-testid="meetings-block-list">
            {meetings.map((m) => {
              const outcomes = Array.isArray(m.outcomes) ? m.outcomes : [];
              const lastOutcome = outcomes.length ? outcomes[outcomes.length - 1] : null;
              return (
                <li
                  key={m.id}
                  className="border border-white/10 rounded-md p-0 hover:border-[#A78BFA]/40 hover:bg-[#A78BFA]/[0.04] transition-colors"
                  data-testid={`meetings-block-row-${m.id}`}
                >
                  <Link
                    to={`/meetings/${m.id}`}
                    className="block p-3"
                    data-testid={`meetings-block-link-${m.id}`}
                  >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-sm truncate">{m.title || "(untitled)"}</div>
                      <div className="flex items-center flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-[#a0aabf] mt-0.5">
                        <span className="inline-flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {fmt(m.start)}
                        </span>
                        {(m.attendees || []).length > 0 && (
                          <span className="inline-flex items-center gap-1">
                            <User className="w-3 h-3" /> {m.attendees.length} attendee{m.attendees.length === 1 ? "" : "s"}
                          </span>
                        )}
                        {m.latest_outcome && (
                          <span className="text-[9px] uppercase font-mono px-1.5 py-0 rounded bg-[#A78BFA]/15 border border-[#A78BFA]/30 text-[#C4B5FD]">
                            {m.latest_outcome}
                          </span>
                        )}
                        {outcomes.length > 0 && (
                          <span className="inline-flex items-center gap-1 text-emerald-300">
                            <MessageSquareText className="w-3 h-3" /> {outcomes.length} note{outcomes.length === 1 ? "" : "s"}
                          </span>
                        )}
                      </div>
                      {lastOutcome?.summary && (
                        <div className="mt-1.5 text-xs text-[#dde3ec] leading-relaxed line-clamp-3">
                          <span className="text-[10px] text-[#647087] font-mono mr-1">
                            latest:
                          </span>
                          {lastOutcome.summary}
                        </div>
                      )}
                    </div>
                    <ExternalLink className="w-3.5 h-3.5 text-[#647087] shrink-0" />
                  </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </FoldBlock>
    </>
  );
}
